import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './ui';
import { AvailableService, listAvailableServices } from '../lib/agentsApi';
import { useTranslation } from 'react-i18next';

interface MCPSearchSelectProps {
  apiUrl: string;
  selected: string[];
  onChange: (ids: string[]) => void;
  /**
   * Excludes the hardcoded demo catalogue entries (`source === 'hardcoded'`,
   * e.g. "ad-tools") — they have no row in `mcp_servers`, so granting access to
   * one silently vanishes on the next fetch (nothing to join against in
   * `agent_mcp_access` queries). Set this wherever the selection results in an
   * actual DB-backed grant, e.g. editing an agent's live MCP access; leave it
   * off for flows like the onboarding wizard that only need a service_id label.
   */
  realOnly?: boolean;
}

/**
 * Large-list-aware MCP multi-select. Loads the available-services catalogue once,
 * then filters client-side behind a debounced query so typing never fires a
 * request per keystroke — behaviour that stays smooth well past 200 servers.
 * The visible result window is capped (MAX_VISIBLE) so the DOM stays light; the
 * count line always reflects the full match total.
 */
const MAX_VISIBLE = 100;

const MCPSearchSelect: React.FC<MCPSearchSelectProps> = ({ apiUrl, selected, onChange, realOnly }) => {
  const { t: tr } = useTranslation();
  const [services, setServices] = useState<AvailableService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rawQuery, setRawQuery] = useState('');
  const [query, setQuery] = useState('');
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listAvailableServices(apiUrl)
      .then((s) => { if (!cancelled) { setServices(realOnly ? s.filter((x) => x.source !== 'hardcoded') : s); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiUrl, realOnly]);

  // Debounce the query used for filtering (250ms).
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => setQuery(rawQuery.trim().toLowerCase()), 250);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [rawQuery]);

  const filtered = useMemo(() => {
    if (!query) return services;
    return services.filter(
      (s) =>
        s.name.toLowerCase().includes(query) ||
        (s.tools || []).some((t) => t.name.toLowerCase().includes(query))
    );
  }, [services, query]);

  const visible = filtered.slice(0, MAX_VISIBLE);
  const selectedSet = new Set(selected);

  const toggle = (id: string) =>
    onChange(selectedSet.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="mcp-select">
      <div className="mcp-select-searchbar">
        <Icon name="search" size={15} />
        <input
          className="ent-input"
          type="text"
          placeholder={tr('components.mCPSearchSelect.searchMCPServersByNameOr')}
          value={rawQuery}
          onChange={(e) => setRawQuery(e.target.value)}
        />
      </div>

      <div className="mcp-select-count">
        {loading ? tr('components.mCPSearchSelect.loadingServers')
          : error ? <span className="mcp-select-error"><Icon name="warning" size={12} /> {error}</span>
          : <>{tr('components.mCPSearchSelect.available', { count: filtered.length })}{query ? ` ${tr('components.mCPSearchSelect.filtered')}` : ''} · <strong>{selected.length}</strong> {tr('components.mCPSearchSelect.selected')}</>}
      </div>

      {!loading && !error && (
        <div className="mcp-select-list">
          {visible.length === 0 ? (
            <div className="mcp-select-empty">{tr('components.mCPSearchSelect.noServersMatch', { v0: rawQuery })}</div>
          ) : (
            visible.map((s) => {
              const checked = selectedSet.has(s.server_id);
              return (
                <label key={s.server_id} className={`mcp-select-row ${checked ? 'checked' : ''}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(s.server_id)} />
                  <span className="mcp-select-name">{s.name}</span>
                  {s.tools && s.tools.length > 0 && (
                    <span className="mcp-select-meta">{tr('components.mCPSearchSelect.toolsCount', { count: s.tools.length })}</span>
                  )}
                  {s.source === 'hardcoded' && <span className="mcp-select-tag">{tr('components.mCPSearchSelect.demo')}</span>}
                </label>
              );
            })
          )}
          {filtered.length > MAX_VISIBLE && (
            <div className="mcp-select-more">{tr('components.mCPSearchSelect.showingFirstOfRefineYourSearch', { v0: MAX_VISIBLE, v1: filtered.length })}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MCPSearchSelect;
